import { useTradeMenu } from '@src/containers/Blotters/Trades/TradeMenu';
import {
  MultilegView,
  colIDToFilterBuilderKey,
  useTradeFilter,
  type BlotterTableTradeFilter,
  type UseTradeFilterOutput,
} from '@src/containers/Blotters/Trades/useTradeFilter';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { useFeatureFlag, useUser } from '@src/hooks';
import { useDisplaySettings, useTradingSettings } from '@src/providers/AppConfigProvider';
import { useContextBlotterFilter } from '@src/providers/ContextBlotterFilterProvider/useContextBlotterFilter';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import { exportOMSTrades } from '@src/utils/blotterQueries';
import {
  BlotterDensity,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  ControlPrefix,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  EMPTY_OBJECT,
  ExportDialog,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  Portal,
  SearchSelect,
  SummaryLine,
  TRADE,
  Toggle,
  WSBlotterTableMaxRecordsReachedWarning,
  createExportFileName,
  filterByColumnMainMenuItems,
  formattedDateForSubscription,
  getStringLabel,
  getTradeAmount,
  getTradeQuantity,
  getTypedKeys,
  isGridApiReady,
  parseDate,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useConstant,
  useDisclosure,
  useDynamicCallback,
  useMixpanel,
  usePersistedBlotterTable,
  useTradeColumns,
  useWsBlotterTable,
  type Column,
  type ExportGridMode,
  type Trade,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, SelectionChangedEvent } from 'ag-grid-enterprise';
import { isEmpty, pick } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { v1 as uuid } from 'uuid';
import { getTradingOrderDetailsRoute } from '../../Routes/routes';
import { DEFAULT_TRADE_COLUMNS } from './columns';
import { TRADES_MULTILEG_VIEW_CONTAINER_ID } from './tokens';

interface FilteredTradesParams {
  /** ID for common blotter type persistence */
  blotterPersistID: string;

  tabLabel?: string;

  orderID?: string;

  defaultColumns?: (keyof Trade | Partial<Column>)[];
  defaultFilter: BlotterTableTradeFilter;
  onCloneTab?: () => void;
  initialIsOpen?: boolean;

  showAPIOrdersToggle?: boolean;
  generateOrderDetailsRoute?: GenerateOrderDetailsRoute;
  showOpenOrderDetails?: boolean;
  persistFilter?: boolean;

  showAllDecimals?: boolean;
  setShowAllDecimals?: (value: boolean) => void;
  /** Layout ID, currently only used for opening order details in the trading layout */
  layoutId: string | undefined;
}

export const FilteredTrades = function FilteredTrades({
  blotterPersistID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  orderID,
  onCloneTab,
  initialIsOpen,
  showAPIOrdersToggle,
  generateOrderDetailsRoute = getTradingOrderDetailsRoute,
  showOpenOrderDetails,
  persistFilter,
  showAllDecimals,
  setShowAllDecimals,
  layoutId,
}: FilteredTradesParams) {
  const mixpanel = useMixpanel();
  const { getConfig } = useOrgConfiguration();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const user = useUser();

  const { filterPropsMutator, additionalFilterState } = useContextBlotterFilter<UseTradeFilterOutput<Trade>>();
  // only show DateRangePicker if it's not used in the additionalFilterState
  const showDateRangePicker = !(additionalFilterState && 'DateRange' in additionalFilterState);

  const { subAccountsByName } = useSubAccounts();
  const { homeCurrency } = useDisplaySettings();

  const [selectedTrades, setSelectedTrades] = useState<Trade[]>([]);

  const columns = useTradeColumns({ defaultColumns: defaultColumns ?? DEFAULT_TRADE_COLUMNS });

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    {
      persistFilter,
    }
  );

  const filteredTrades = useTradeFilter({
    persistedBlotterTable,
    filterByOrderID: !!orderID,
    additionalFilterState,
    defaultMultilegView: defaultFilter.MultilegView,
  });
  const {
    clientSideFilter: clientLocalFilter,
    blotterTableFilterProps,
    changeFilter,
    filterBuilderProps,
    filterableProperties,
  } = useMemo(() => filterPropsMutator(filteredTrades), [filterPropsMutator, filteredTrades]);

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { openClause } = filterBuilderAccordion;

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<Trade>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const selectedRows = api.getSelectedRows();
    setSelectedTrades(selectedRows);
  }, []);

  const blotterTag = useConstant(`FILTERED_TRADES_${uuid()}`);

  const exportDialog = useDisclosure();

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({
        params,
        colIDToFilterBuilderKey,
        openClause,
        mixpanel,
      });
    },
    [mixpanel, openClause]
  );
  const { getContextMenuItems, dialogs } = useTradeMenu({
    openClause,
    filterableProperties,
    generateOrderDetailsRoute,
    showOpenOrderDetails,
    layoutId,
  });

  const handleExportTrades = useCallback(() => {
    mixpanel.track(MixpanelEvent.ExportTrades);
    const serverFilter = {
      ...(onlyServerFilterKeys(filteredTrades.filter) ?? {}),
      ...(orderID != null && { OrderID: orderID }),
    };
    const stringFilters = getTypedKeys(serverFilter)
      .map(key => {
        const value = serverFilter[key];
        if (isEmpty(value)) {
          return undefined;
        }
        switch (key) {
          case 'StartDate':
          case 'EndDate':
            return undefined;
          case 'Symbols':
            return `Symbol:${value as (typeof serverFilter)[typeof key]}`;
          case 'SubAccounts':
            // Map sub account Names to their IDs to make this API happy
            return `SubAccountID:${(value as NonNullable<(typeof serverFilter)[typeof key]>)
              .map(subAcctName => subAccountsByName?.get(subAcctName)?.SubaccountID || undefined)
              .compact()}`;
        }
        return `${key}:${value}`;
      })
      .compact();
    const query = {
      startDate: formattedDateForSubscription(serverFilter.StartDate),
      endDate: formattedDateForSubscription(serverFilter.EndDate || parseDate(null)),
      orderBy: '-TransactTime',
      filter: stringFilters.length > 0 ? stringFilters : undefined,
    };
    if (!query.startDate) {
      // Backend doesn't like an empty startDate, but is fine if we delete it
      delete query.startDate;
    }
    exportOMSTrades(user.OrgApiEndpoint, query);
  }, [mixpanel, filteredTrades.filter, orderID, user.OrgApiEndpoint, subAccountsByName]);

  const { showBlotterPauseButton, enableMultilegTradeFilter } = useFeatureFlag();

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const request = useMemo(
    () => ({
      name: TRADE,
      tag: blotterTag,
      sort_by: '-TransactTime',
      ...(orderID != null && { OrderID: orderID }),
      ...removeEmptyFilters(onlyServerFilterKeys(filteredTrades.filter)),
    }),
    [blotterTag, orderID, filteredTrades.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    rowID: 'TradeID',
    density: BlotterDensity.Compact,
    clientLocalFilter,
    columns,
    getExtraMainMenuItems,
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    startingRowLimit: getConfig(OrgConfigurationKey.TradeRowsMax, DEFAULT_MAX_ROWS),
    settings: blotterTableSettings,
    gridOptions: {
      onSelectionChanged: handleRowSelectionChanged,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems,
    },
    exportOptions: {
      csvParams: EMPTY_OBJECT,
      excelParams: EMPTY_OBJECT,
      name: 'Trades',
      tabLabel: tabLabel,
    },
  });

  const handleExport = useCallback(
    (mode: ExportGridMode) => {
      const exportFunc = mode === 'CSV' ? blotterTable.exportDataAsCSV : blotterTable.exportDataAsExcel;
      exportFunc({
        fileName: createExportFileName({
          name: 'Trades',
          tabLabel,
          mode,
        }),
      });
    },
    [blotterTable.exportDataAsCSV, blotterTable.exportDataAsExcel, tabLabel]
  );

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleOnApiOrdersChange = useDynamicCallback((value: boolean) => {
    mixpanel.track(MixpanelEvent.ApiOrdersToggle, { [MixpanelEventProperty.Enabled]: value });
    changeFilter(curr => ({ ...curr, HideApiCalls: !value }));
  });

  const multilegViewOptions = useMemo(() => [MultilegView.Parent, MultilegView.Child, MultilegView.Both], []);

  const handleMultilegViewChange = useCallbackRef((view: MultilegView | undefined) => {
    if (view != null) {
      changeFilter(curr => ({ ...curr, MultilegView: view }));
    }
  });

  return (
    <>
      {enableMultilegTradeFilter && (
        <Portal portalId={TRADES_MULTILEG_VIEW_CONTAINER_ID}>
          <SearchSelect
            options={multilegViewOptions}
            selection={filteredTrades.filter.MultilegView ?? MultilegView.Both}
            getLabel={getStringLabel}
            onChange={handleMultilegViewChange}
            size={FormControlSizes.Small}
            showDropdownSearch={false}
            portalize
            dropdownWidth="90px"
            prefix={<ControlPrefix>Multileg Trades</ControlPrefix>}
            data-testid="multileg-view-select"
          />
        </Portal>
      )}
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={showDateRangePicker}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            {showAPIOrdersToggle && (
              <>
                <Toggle
                  label="API Orders"
                  size={FormControlSizes.Small}
                  tooltip="Include trades from orders submitted through the API in the results."
                  checked={!filteredTrades.filter.HideApiCalls}
                  onChange={handleOnApiOrdersChange}
                />
              </>
            )}
            {!showAllDecimalsInBlotters && setShowAllDecimals != null && (
              <Toggle
                label="Show All Decimals"
                size={FormControlSizes.Small}
                checked={showAllDecimals ?? false}
                onChange={setShowAllDecimals}
              />
            )}
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}

            <Button
              startIcon={IconName.DocumentDownload}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              data-testid="export-csv"
              onClick={() => {
                mixpanel.track(MixpanelEvent.ExportRowsModalOpen);
                exportDialog.open();
              }}
            >
              Export
            </Button>
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning
        {...blotterTable.paginationLimit}
        getTimestamp={trade => trade.TransactTime}
      />
      <BlotterTable {...blotterTable} />

      <SummaryLine
        homeCurrency={homeCurrency}
        rows={selectedTrades}
        getQuantity={getTradeQuantity}
        getAmount={getTradeAmount}
      />
      <ExportDialog
        {...exportDialog}
        handleExportLocalData={handleExport}
        handleExportServerData={handleExportTrades}
      />
      {dialogs}
    </>
  );
};

function onlyServerFilterKeys(filter: BlotterTableTradeFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return pick(filter, [
    'StartDate',
    'EndDate',
    'Statuses',
    'SubAccounts',
    'Symbols',
    'HideApiCalls',
    'OrderID',
    'RFQID',
    'Markets',
  ]);
}
